import { useEffect, useState } from "react";
import { X, ArrowRight, Sparkles, Zap, Gift } from "lucide-react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "lma_onboarding_v1_seen";

const steps = [
  {
    icon: Sparkles,
    title: "Välkommen till Access!",
    body: "Här på översikten ser du alla dina förmåner, kommande event och ditt digitala medlemskort.",
  },
  {
    icon: Zap,
    title: "Aktivera förmåner i butik",
    body: "Tryck på en förmån under \"Mina förmåner\" för att få en 6-siffrig kod. Visa koden i kassan när du löser in den.",
  },
  {
    icon: Gift,
    title: "Bjud in en vän — få en gratis månad",
    body: "Dela din inbjudningslänk via \"Bjud in\". När din vän blir medlem får ni båda en gratis månad.",
  },
] as const;

export function OnboardingTour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        // Vänta lite så dashboarden hinner rendera
        const t = setTimeout(() => setOpen(true), 600);
        return () => clearTimeout(t);
      }
    } catch {}
  }, []);

  function close() {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
    setOpen(false);
  }

  if (!open) return null;
  const s = steps[step];
  const Icon = s.icon;
  const isLast = step === steps.length - 1;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Välkomstrundtur"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden relative">
        <button
          onClick={close}
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-foreground/60 hover:text-foreground hover:bg-muted transition"
          aria-label="Hoppa över rundtur"
        >
          <X size={18} />
        </button>

        <div className="p-7 sm:p-8">
          <div className="w-14 h-14 rounded-full bg-[var(--color-gold)]/15 flex items-center justify-center mb-5">
            <Icon className="text-[var(--color-gold)]" size={26} />
          </div>
          <h2 className="font-display font-bold text-2xl">{s.title}</h2>
          <p className="text-muted-foreground mt-3 leading-relaxed text-sm">{s.body}</p>

          <div className="flex items-center gap-1.5 mt-7">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? "w-6 bg-[var(--color-primary)]" : "w-1.5 bg-border"
                }`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between mt-6">
            <button onClick={close} className="text-sm text-muted-foreground hover:text-foreground">
              Hoppa över
            </button>
            {isLast ? (
              <Link
                to="/access/formaner"
                onClick={close}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] font-display font-semibold text-sm transition"
              >
                Visa förmåner <ArrowRight size={14} />
              </Link>
            ) : (
              <button
                onClick={() => setStep(step + 1)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)] font-display font-semibold text-sm transition"
              >
                Nästa <ArrowRight size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
